import React from 'react';
import { TemplateFrame } from '../TemplateFrame';
import { Check, X, Stamp } from 'lucide-react';
const FN_ICON = 'https://cdn.magicpatterns.com/uploads/6S42RJUThA4LVcV5MgRYxz/filmnotes-fn-full-icon_1024.png';
const FN_RED = 'https://cdn.magicpatterns.com/uploads/oTP2o8fSQ2isLxJ5nM9Smk/filmnotes-fn-red-icon_1024.png';
const FN_WORDMARK = 'https://cdn.magicpatterns.com/uploads/n83aYeAfPgByVNUDm9vCqj/filmnotes-longtext-icon.png';
// Tertiary tokens
const C_YELLOW = '#F0B92B';
const C_AMBER = '#D9711F';
const C_BURGUNDY = '#8B2E3F';
const C_VIOLET = '#5B3A82';
const C_GREEN = '#2D7A4F';
const C_BLUE = '#2E5C8A';
function SectionHeader({
  id,
  title,
  sub
}: {
  id: string;
  title: string;
  sub: string;
}) {
  return <div className="mb-8" data-id="element-1207">
      <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-1" data-id="element-1208">
        {id}
      </p>
      <h2 className="font-display font-bold text-2xl text-film-black" data-id="element-1209">
        {title}
      </h2>
      <p className="text-sm text-film-gray mt-1 max-w-xl" data-id="element-1210">{sub}</p>
    </div>;
}
// Simple QR placeholder grid — reusable
function MiniQR({
  size = 'sm'
}: {
  size?: 'xs' | 'sm' | 'md' | 'lg';
}) {
  const dims = {
    xs: 'w-7 h-7 p-1',
    sm: 'w-10 h-10 p-1',
    md: 'w-14 h-14 p-1.5',
    lg: 'w-20 h-20 p-2'
  };
  return <div className={`bg-film-black ${dims[size]}`} data-id="element-1211">
      <div className="w-full h-full grid grid-cols-4 grid-rows-4 gap-px" data-id="element-1212">
        {Array.from({
        length: 16
      }).map((_, i) => <div key={i} className={[0, 2, 5, 7, 8, 10, 13, 15].includes(i) ? 'bg-white' : 'bg-transparent'} data-id="element-1213" />)}
      </div>
    </div>;
}
// Transparent vinyl indicator (checkerboard pattern)
function TransparentVinyl({
  children
}: {
  children?: React.ReactNode;
}) {
  return <div className="relative w-full h-full" style={{
    backgroundImage: 'linear-gradient(45deg, #d4d4d4 25%, transparent 25%), linear-gradient(-45deg, #d4d4d4 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d4d4d4 75%), linear-gradient(-45deg, transparent 75%, #d4d4d4 75%)',
    backgroundSize: '8px 8px',
    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
    backgroundColor: '#fafafa'
  }} data-id="element-1214">
      {children}
    </div>;
}
export function PrintOfflinePage() {
  return <div className="max-w-6xl mx-auto py-12 px-8 space-y-20" data-id="element-1215">
      {/* Header */}
      <div data-id="element-1216">
        <p className="font-mono text-xs text-film-gray mb-3" data-id="element-1217">
          04_TEMPLATES_PRINT_OFFLINE
        </p>
        <h1 className="font-display font-bold text-4xl text-film-black mb-2" data-id="element-1218">
          Print & Offline
        </h1>
        <p className="text-film-gray max-w-2xl" data-id="element-1219">
          Lab cards in four sizes, sticker sheet with clear-vinyl variants,
          postcard, and full print specs. CMYK · 3mm bleed · 350gsm uncoated
          unless otherwise noted.
        </p>
      </div>

      {/* ============================================ */}
      {/* LAB CARDS · multi-format                     */}
      {/* ============================================ */}
      <section data-id="element-1220">
        <SectionHeader id="§ LAB_CARDS" title="Lab Cards · Four Formats" sub="Drop at labs with permission. Each format has a different use: compact, standard, hero, flyer." data-id="element-1221" />

        {/* Format strip */}
        <div className="bg-white border border-film-border shadow-card mb-6" data-id="element-1222">
          <div className="grid grid-cols-4" data-id="element-1223">
            {[{
            name: 'Square',
            size: '90×90mm',
            use: 'Counter chip'
          }, {
            name: 'A6',
            size: '105×148mm',
            use: 'Standard drop card'
          }, {
            name: '4:5 Studio',
            size: '100×125mm',
            use: 'Partner edition'
          }, {
            name: 'A5 Flyer',
            size: '148×210mm',
            use: 'Lab partner kit'
          }].map((f, i) => <div key={f.name} className={`p-4 ${i < 3 ? 'border-r border-film-border' : ''}`} data-id="element-1224">
                <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray mb-1" data-id="element-1225">
                  Format {(i + 1).toString().padStart(2, '0')}
                </p>
                <p className="font-display font-bold text-base text-film-black leading-tight" data-id="element-1226">
                  {f.name}
                </p>
                <p className="font-mono text-[10px] text-film-black mt-0.5" data-id="element-1227">
                  {f.size}
                </p>
                <p className="text-[11px] text-film-gray mt-1" data-id="element-1228">{f.use}</p>
              </div>)}
          </div>
        </div>

        <div className="flex flex-wrap gap-7" data-id="element-1229">
          {/* ── Square 90×90mm — Ektar Blue ── */}
          <TemplateFrame componentId="FMT_PRINT / LAB_CARD · Square" dimensions="90×90mm · 1:1" caption="Compact counter chip. Ektar Blue base." width="220px" data-id="element-1230">
            <div className="relative w-full flex flex-col" style={{
            aspectRatio: '1/1',
            backgroundColor: C_BLUE
          }} data-id="element-1231">
              <div className="absolute inset-3 border border-dashed border-white/20 pointer-events-none" data-id="element-1232">
                <span className="absolute -top-2 left-1 px-1 font-mono text-[7px] text-white/40 uppercase tracking-wider" style={{
                backgroundColor: C_BLUE
              }} data-id="element-1233">
                  Quiet
                </span>
              </div>
              <div className="relative z-10 p-5 flex flex-col h-full text-white" data-id="element-1234">
                <img src={FN_ICON} className="w-7 h-7 brightness-0 invert" alt="" data-id="element-1235" />
                <h3 className="font-display font-bold text-xl leading-[0.95] mt-3" data-id="element-1236">
                  Find your
                  <br data-id="element-1237" />
                  lab.
                </h3>
                <div className="flex-1" data-id="element-1238" />
                <div className="flex items-end justify-between" data-id="element-1239">
                  <MiniQR size="sm" data-id="element-1240" />
                  <p className="font-mono text-[8px] uppercase tracking-wider text-white/80" data-id="element-1241">
                    filmnotes
                    <br data-id="element-1242" />
                    .app
                  </p>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* ── A6 Front 105×148mm — white + cream ── */}
          <TemplateFrame componentId="FMT_PRINT / LAB_CARD · A6 Front" dimensions="105×148mm · A6" caption="Standard A6 face. White + cream lower-third." width="220px" data-id="element-1243">
            <div className="relative bg-white w-full overflow-hidden" style={{
            aspectRatio: '105/148'
          }} data-id="element-1244">
              <div className="absolute left-0 right-0 bottom-0 bg-film-cream" style={{
              height: '38%'
            }} data-id="element-1245" />
              <div className="absolute inset-3 border border-dashed border-film-gray/30 pointer-events-none" data-id="element-1246">
                <span className="absolute -top-2 left-1 bg-white px-1 font-mono text-[7px] text-film-gray/60 uppercase tracking-wider" data-id="element-1247">
                  Quiet
                </span>
              </div>
              <div className="relative z-10 flex flex-col h-full p-5" data-id="element-1248">
                <img src={FN_ICON} className="w-8 h-8 mb-3" alt="" data-id="element-1249" />
                <h3 className="font-display font-bold text-xl text-film-black leading-[0.92] mb-1" data-id="element-1250">
                  Log rolls.
                  <br data-id="element-1251" />
                  Find labs.
                </h3>
                <p className="font-mono text-[8px] text-film-gray mt-1 uppercase tracking-wider" data-id="element-1252">
                  The film photography hub
                </p>
                <div className="flex-1" data-id="element-1253" />
                <div className="flex items-end justify-between" data-id="element-1254">
                  <MiniQR size="md" data-id="element-1255" />
                  <p className="font-mono text-[9px] text-film-black uppercase tracking-wider" data-id="element-1256">
                    filmnotes.app
                  </p>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* ── 4:5 Studio 100×125mm — Fuji Green stripe ── */}
          <TemplateFrame componentId="FMT_PRINT / LAB_CARD · 4:5 Studio" dimensions="100×125mm · 4:5" caption="Partner edition. Green stripe = active partnership." width="220px" data-id="element-1257">
            <div className="relative bg-white w-full overflow-hidden" style={{
            aspectRatio: '4/5'
          }} data-id="element-1258">
              {/* Green left stripe */}
              <div className="absolute left-0 top-0 bottom-0" style={{
              width: '14px',
              backgroundColor: C_GREEN
            }} data-id="element-1259" />
              <div className="absolute inset-3 border border-dashed border-film-gray/30 pointer-events-none ml-3" data-id="element-1260" />
              <div className="relative z-10 flex flex-col h-full p-5 pl-7" data-id="element-1261">
                <div className="flex items-center justify-between" data-id="element-1262">
                  <img src={FN_ICON} className="w-7 h-7" alt="" data-id="element-1263" />
                  <span className="font-mono text-[8px] uppercase tracking-wider text-white px-1.5 py-0.5" style={{
                  backgroundColor: C_GREEN
                }} data-id="element-1264">
                    Partner
                  </span>
                </div>

                <h3 className="font-display font-bold text-lg text-film-black leading-[0.95] mt-4" data-id="element-1265">
                  Photo Works
                  <br data-id="element-1266" />
                  Belfast.
                </h3>
                <p className="font-mono text-[8px] text-film-gray uppercase tracking-wider mt-1" data-id="element-1267">
                  est. 1987 · UK
                </p>

                <div className="flex-1" data-id="element-1268" />

                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[8px] mb-3" data-id="element-1269">
                  {[['C-41', '24h'], ['B&W', '48h'], ['Scan', '4K'], ['Print', 'C-Type']].map(([a, b]) => <div key={a} className="flex justify-between font-mono" data-id="element-1270">
                      <span className="text-film-gray" data-id="element-1271">{a}</span>
                      <span className="text-film-black" data-id="element-1272">{b}</span>
                    </div>)}
                </div>

                <div className="flex items-end justify-between pt-2 border-t border-film-border" data-id="element-1273">
                  <MiniQR size="sm" data-id="element-1274" />
                  <p className="font-mono text-[8px] text-film-black uppercase tracking-wider text-right leading-tight" data-id="element-1275">
                    filmnotes
                    <br data-id="element-1276" />
                    .app
                  </p>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* ── A5 Flyer 148×210mm — Darkroom Burgundy hero ── */}
          <TemplateFrame componentId="FMT_PRINT / LAB_CARD · A5 Flyer" dimensions="148×210mm · A5" caption="Lab kit hero. Burgundy header, full info below." width="240px" data-id="element-1277">
            <div className="relative bg-white w-full overflow-hidden" style={{
            aspectRatio: '148/210'
          }} data-id="element-1278">
              {/* Burgundy header */}
              <div className="px-5 py-4 text-white" style={{
              backgroundColor: C_BURGUNDY
            }} data-id="element-1279">
                <div className="flex items-center justify-between" data-id="element-1280">
                  <img src={FN_ICON} className="w-6 h-6 brightness-0 invert" alt="" data-id="element-1281" />
                  <span className="font-mono text-[8px] uppercase tracking-wider text-white/70" data-id="element-1282">
                    Lab kit · v2
                  </span>
                </div>
                <h3 className="font-display font-bold text-lg leading-[0.95] mt-3" data-id="element-1283">
                  Drop rolls.
                  <br data-id="element-1284" />
                  Track them.
                </h3>
                <p className="font-mono text-[8px] uppercase tracking-wider text-white/70 mt-2" data-id="element-1285">
                  Free for shooters · iOS
                </p>
              </div>

              {/* Body */}
              <div className="p-5" data-id="element-1286">
                <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-2" data-id="element-1287">
                  What you get
                </p>
                <ul className="space-y-1.5" data-id="element-1288">
                  {['Track every roll', 'Push/pull notes', 'Find labs nearby', 'Offline first'].map(b => <li key={b} className="text-[10px] font-display font-bold text-film-black flex gap-2 items-baseline leading-tight" data-id="element-1289">
                      <span className="w-1 h-1 shrink-0 translate-y-[-1px]" style={{
                    backgroundColor: C_BURGUNDY
                  }} data-id="element-1290" />
                      {b}
                    </li>)}
                </ul>

                <div className="mt-4 flex items-end justify-between pt-3 border-t border-film-border" data-id="element-1291">
                  <MiniQR size="md" data-id="element-1292" />
                  <div className="text-right" data-id="element-1293">
                    <p className="font-mono text-[8px] uppercase tracking-wider" style={{
                    color: C_BURGUNDY
                  }} data-id="element-1294">
                      Scan to install
                    </p>
                    <p className="font-mono text-[8px] text-film-black mt-0.5" data-id="element-1295">
                      filmnotes.app
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TemplateFrame>
        </div>

        {/* Original A6 Back keeps its slot */}
        <div className="mt-8" data-id="element-1296">
          <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-4" data-id="element-1297">
            A6 · Back face
          </p>
          <TemplateFrame componentId="FMT_PRINT / LAB_CARD · A6 Back" dimensions="105×148mm · A6" caption="A6 reverse. Bullets + lab partnership zone." width="220px" data-id="element-1298">
            <div className="relative bg-white w-full overflow-hidden" style={{
            aspectRatio: '105/148'
          }} data-id="element-1299">
              <div className="absolute inset-3 border border-dashed border-film-gray/30 pointer-events-none" data-id="element-1300" />
              <div className="relative z-10 flex flex-col h-full p-5" data-id="element-1301">
                <p className="font-mono text-[9px] uppercase tracking-wider mb-3" style={{
                color: C_BURGUNDY
              }} data-id="element-1302">
                  What it does
                </p>
                <ul className="space-y-2 text-film-black mb-4" data-id="element-1303">
                  {['Track every roll', 'Log frames + push/pull', 'Find your lab', 'Offline first'].map(b => <li key={b} className="text-sm font-display font-bold flex items-baseline gap-2 leading-tight" data-id="element-1304">
                      <span className="w-1 h-1 shrink-0 translate-y-[-2px]" style={{
                    backgroundColor: C_BURGUNDY
                  }} data-id="element-1305" />
                      {b}
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-1306">
                  <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-1.5" data-id="element-1307">
                    In partnership with
                  </p>
                  <div className="bg-film-cream border border-dashed border-film-gray/40 h-10 flex items-center justify-center" data-id="element-1308">
                    <span className="font-mono text-[8px] text-film-gray/60 uppercase tracking-wider" data-id="element-1309">
                      [Lab logo zone]
                    </span>
                  </div>
                  <p className="font-mono text-[7px] text-film-gray/60 mt-2 text-center uppercase tracking-wider" data-id="element-1310">
                    © 2026 Film Notes · filmnotes.app
                  </p>
                </div>
              </div>
            </div>
          </TemplateFrame>
        </div>
      </section>

      {/* ============================================ */}
      {/* STICKER SHEET                                */}
      {/* ============================================ */}
      <section data-id="element-1311">
        <SectionHeader id="§ STICKER_SHEET" title="Sticker Sheet · 6 Variants" sub="Vinyl stickers in opaque & clear. The clear-center variants mimic the app's 'logged frame' status — they sit over surfaces and frame what's underneath." data-id="element-1312" />
        <div className="flex flex-wrap gap-7 items-end" data-id="element-1313">
          {/* Circular red logo */}
          <TemplateFrame componentId="FMT_PRINT / STICKER · Circular" dimensions="60mm Ø · Circular" caption="Red, white fn icon. Camera bags / journals." width="200px" data-id="element-1314">
            <div className="relative w-full bg-film-cream p-4 flex items-center justify-center" style={{
            aspectRatio: '1/1'
          }} data-id="element-1315">
              <div className="absolute inset-2 border border-dashed border-film-red/40 rounded-full pointer-events-none" data-id="element-1316">
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-film-cream px-1 font-mono text-[7px] text-film-red/60 uppercase tracking-wider" data-id="element-1317">
                  Bleed +3mm
                </span>
              </div>
              <div className="w-[78%] aspect-square bg-film-red rounded-full flex items-center justify-center relative" data-id="element-1318">
                <div className="absolute inset-1.5 border border-white/15 rounded-full pointer-events-none" data-id="element-1319" />
                <img src={FN_RED} className="w-1/2 brightness-0 invert" alt="" data-id="element-1320" />
              </div>
            </div>
          </TemplateFrame>

          {/* Square black mantra */}
          <TemplateFrame componentId="FMT_PRINT / STICKER · Square Mantra" dimensions="50×50mm · Square" caption="Black with mantra. Laptops, hard cases." width="200px" data-id="element-1321">
            <div className="relative w-full bg-film-cream p-4 flex items-center justify-center" style={{
            aspectRatio: '1/1'
          }} data-id="element-1322">
              <div className="absolute inset-2 border border-dashed border-film-red/40 pointer-events-none" data-id="element-1323">
                <span className="absolute -top-2 left-1 bg-film-cream px-1 font-mono text-[7px] text-film-red/60 uppercase tracking-wider" data-id="element-1324">
                  Bleed
                </span>
              </div>
              <div className="w-full aspect-square bg-film-black flex flex-col items-center justify-center p-3 text-center" data-id="element-1325">
                <p className="font-display font-bold text-white text-base leading-[1.15]" data-id="element-1326">
                  SHOOT
                </p>
                <div className="h-px w-4 bg-white/30 my-1.5" data-id="element-1327" />
                <p className="font-display font-bold text-white text-base leading-[1.15]" data-id="element-1328">
                  LOG
                </p>
                <div className="h-px w-4 bg-white/30 my-1.5" data-id="element-1329" />
                <p className="font-display font-bold text-white text-base leading-[1.15]" data-id="element-1330">
                  SHARE
                </p>
              </div>
            </div>
          </TemplateFrame>

          {/* Die-cut wordmark */}
          <TemplateFrame componentId="FMT_PRINT / STICKER · Wordmark" dimensions="80×30mm · Die-cut" caption="Wordmark only. Lab counters, bumpers." width="240px" data-id="element-1331">
            <div className="relative w-full bg-film-cream p-3 flex items-center justify-center" style={{
            aspectRatio: '80/30'
          }} data-id="element-1332">
              <div className="absolute inset-1.5 border border-dashed border-film-red/40 pointer-events-none" data-id="element-1333">
                <span className="absolute -top-2 left-1 bg-film-cream px-1 font-mono text-[7px] text-film-red/60 uppercase tracking-wider" data-id="element-1334">
                  Bleed
                </span>
              </div>
              <div className="bg-white px-3 py-2 flex items-center justify-center" data-id="element-1335">
                <img src={FN_WORDMARK} className="h-4" alt="" data-id="element-1336" />
              </div>
            </div>
          </TemplateFrame>

          {/* Logged-in badge pill */}
          <TemplateFrame componentId="FMT_PRINT / STICKER · Logged-in Badge" dimensions="100×30mm · Pill" caption="For lab counters. Signals partnership." width="240px" data-id="element-1337">
            <div className="relative w-full bg-white p-3 flex items-center justify-center" style={{
            aspectRatio: '100/30'
          }} data-id="element-1338">
              <div className="absolute inset-1.5 border border-dashed border-film-red/40 pointer-events-none" data-id="element-1339">
                <span className="absolute -top-2 left-1 bg-white px-1 font-mono text-[7px] text-film-red/60 uppercase tracking-wider" data-id="element-1340">
                  Bleed
                </span>
              </div>
              <div className="bg-film-cream px-3 py-1.5 flex items-center gap-2 rounded-full" style={{
              border: `1px solid ${C_BURGUNDY}33`
            }} data-id="element-1341">
                <img src={FN_ICON} className="w-3.5 h-3.5" alt="" data-id="element-1342" />
                <span className="font-mono text-[9px] text-film-black uppercase tracking-wider" data-id="element-1343">
                  Logged in Film Notes
                </span>
              </div>
            </div>
          </TemplateFrame>

          {/* NEW · Transparent FRAME sticker (mimics logged frame) */}
          <TemplateFrame componentId="FMT_PRINT / STICKER · Clear Frame" dimensions="70×70mm · Clear vinyl" caption="Clear-center sticker. Mimics the app's 'logged frame' — peel and frame anything." width="240px" data-id="element-1344">
            <div className="relative w-full p-4 flex items-center justify-center" style={{
            aspectRatio: '1/1',
            backgroundColor: '#eaeaea'
          }} data-id="element-1345">
              <div className="absolute inset-2 border border-dashed border-film-red/40 pointer-events-none" data-id="element-1346">
                <span className="absolute -top-2 left-1 bg-[#eaeaea] px-1 font-mono text-[7px] text-film-red/60 uppercase tracking-wider" data-id="element-1347">
                  Bleed · clear vinyl
                </span>
              </div>

              {/* The sticker itself — outer red frame, transparent center */}
              <div className="relative w-[82%] aspect-square" data-id="element-1348">
                {/* Outer red frame */}
                <div className="absolute inset-0 border-[10px]" style={{
                borderColor: C_BURGUNDY
              }} data-id="element-1349" />
                {/* Top label */}
                <div className="absolute top-0 left-0 right-0 h-[18%] flex items-center justify-between px-2" style={{
                backgroundColor: C_BURGUNDY
              }} data-id="element-1350">
                  <span className="font-mono text-[7px] uppercase tracking-wider text-white" data-id="element-1351">
                    Loaded
                  </span>
                  <img src={FN_ICON} className="w-2.5 h-2.5 brightness-0 invert" alt="" data-id="element-1352" />
                </div>
                {/* Bottom label */}
                <div className="absolute bottom-0 left-0 right-0 h-[14%] flex items-center justify-center" style={{
                backgroundColor: C_BURGUNDY
              }} data-id="element-1353">
                  <span className="font-mono text-[6px] uppercase tracking-wider text-white" data-id="element-1354">
                    Filmnotes · frame 01
                  </span>
                </div>
                {/* Transparent center */}
                <div className="absolute" style={{
                top: '18%',
                bottom: '14%',
                left: '10px',
                right: '10px'
              }} data-id="element-1355">
                  <TransparentVinyl data-id="element-1356">
                    <span className="absolute inset-0 flex items-center justify-center font-mono text-[7px] uppercase tracking-wider text-film-gray/60" data-id="element-1357">
                      clear
                    </span>
                  </TransparentVinyl>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* NEW · Polaroid-shaped Clear Vinyl sticker */}
          <TemplateFrame componentId="FMT_PRINT / STICKER · Polaroid Frame" dimensions="80×95mm · Clear vinyl" caption="Polaroid-shaped clear sticker. Stick over a print or surface." width="220px" data-id="element-1358">
            <div className="relative w-full p-3 flex items-center justify-center" style={{
            aspectRatio: '1/1',
            backgroundColor: '#eaeaea'
          }} data-id="element-1359">
              <div className="absolute inset-2 border border-dashed border-film-red/40 pointer-events-none" data-id="element-1360">
                <span className="absolute -top-2 left-1 bg-[#eaeaea] px-1 font-mono text-[7px] text-film-red/60 uppercase tracking-wider" data-id="element-1361">
                  Bleed
                </span>
              </div>

              {/* Polaroid form */}
              <div className="relative w-[78%]" style={{
              aspectRatio: '80/95'
            }} data-id="element-1362">
                {/* White polaroid body */}
                <div className="absolute inset-0 bg-white shadow-sm" data-id="element-1363" />
                {/* Transparent window */}
                <div className="absolute" style={{
                top: '8%',
                bottom: '24%',
                left: '8%',
                right: '8%'
              }} data-id="element-1364">
                  <TransparentVinyl data-id="element-1365">
                    <span className="absolute inset-0 flex items-center justify-center font-mono text-[7px] uppercase tracking-wider text-film-gray/60" data-id="element-1366">
                      clear
                    </span>
                  </TransparentVinyl>
                </div>
                {/* Bottom caption */}
                <div className="absolute bottom-[6%] left-[8%] right-[8%] flex items-center justify-between" style={{
                height: '14%'
              }} data-id="element-1367">
                  <span className="font-display font-bold text-[9px]" style={{
                  color: C_BURGUNDY
                }} data-id="element-1368">
                    shot on film
                  </span>
                  <img src={FN_ICON} className="w-3 h-3" alt="" data-id="element-1369" />
                </div>
              </div>
            </div>
          </TemplateFrame>
        </div>
      </section>

      {/* ============================================ */}
      {/* POSTCARD                                      */}
      {/* ============================================ */}
      <section data-id="element-1370">
        <SectionHeader id="§ POSTCARD" title="Postcard · A6 Landscape" sub="148×105mm · For events and lab partner kits. Front = community scan, back = classic split address card." data-id="element-1371" />
        <div className="flex flex-wrap gap-8" data-id="element-1372">
          {/* Front */}
          <TemplateFrame componentId="FMT_PRINT / POSTCARD · Front" dimensions="148×105mm · A6 landscape" caption="Full-bleed community scan + tiny fn corner mark." width="320px" data-id="element-1373">
            <div className="relative w-full bg-film-black" style={{
            aspectRatio: '148/105'
          }} data-id="element-1374">
              <img src="https://images.unsplash.com/photo-1495121553079-4c61bcce1894?w=800&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-1375" />
              <div className="absolute bottom-2 right-2 bg-white/95 backdrop-blur px-1.5 py-0.5 flex items-center gap-1" data-id="element-1376">
                <img src={FN_ICON} className="w-3 h-3" alt="" data-id="element-1377" />
                <span className="font-mono text-[7px] text-film-black uppercase tracking-wider" data-id="element-1378">
                  fn
                </span>
              </div>
            </div>
          </TemplateFrame>

          {/* Back */}
          <TemplateFrame componentId="FMT_PRINT / POSTCARD · Back" dimensions="148×105mm · A6 landscape" caption="Left: handwritten note. Right: address + stamp + QR." width="320px" data-id="element-1379">
            <div className="relative w-full bg-white flex" style={{
            aspectRatio: '148/105'
          }} data-id="element-1380">
              <div className="w-1/2 p-4 flex flex-col border-r border-film-border" data-id="element-1381">
                <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-2" data-id="element-1382">
                  Notes
                </p>
                <div className="flex-1 space-y-2.5" data-id="element-1383">
                  {Array.from({
                  length: 5
                }).map((_, i) => <div key={i} className="h-px bg-film-border" data-id="element-1384" />)}
                </div>
                <p className="font-mono text-[7px] text-film-gray/60 mt-2 uppercase tracking-wider" data-id="element-1385">
                  © 2026 Film Notes
                </p>
              </div>

              <div className="w-1/2 p-4 flex flex-col" data-id="element-1386">
                <div className="self-end w-12 h-14 border border-dashed border-film-gray/50 flex flex-col items-center justify-center" data-id="element-1387">
                  <Stamp className="w-3 h-3 text-film-gray/60" data-id="element-1388" />
                  <span className="font-mono text-[6px] text-film-gray/60 mt-1 uppercase" data-id="element-1389">
                    Stamp
                  </span>
                </div>
                <div className="mt-3 space-y-2" data-id="element-1390">
                  {Array.from({
                  length: 4
                }).map((_, i) => <div key={i} className="h-px bg-film-border" data-id="element-1391" />)}
                </div>
                <div className="mt-auto flex items-end justify-between" data-id="element-1392">
                  <div data-id="element-1393">
                    <p className="font-mono text-[7px] text-film-gray uppercase tracking-wider mb-1" data-id="element-1394">
                      Scan
                    </p>
                    <MiniQR size="sm" data-id="element-1395" />
                  </div>
                  <img src={FN_ICON} className="w-5 h-5" alt="" data-id="element-1396" />
                </div>
              </div>
            </div>
          </TemplateFrame>
        </div>
      </section>

      {/* ============================================ */}
      {/* QR PLACEMENT RULES                            */}
      {/* ============================================ */}
      <section data-id="element-1397">
        <SectionHeader id="§ QR_RULES" title="QR Placement Rules" sub="Three constraints that keep scans reliable in lab light and on real surfaces." data-id="element-1398" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1399">
          {/* Minimum size */}
          <div className="bg-white border border-film-border p-6 shadow-card relative" data-id="element-1400">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-film-red" data-id="element-1401" />
            <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-2" data-id="element-1402">
              Rule 01 · Minimum size
            </p>
            <p className="font-display font-bold text-3xl text-film-black mb-1" data-id="element-1403">
              25mm
            </p>
            <p className="text-xs text-film-gray leading-snug mb-4" data-id="element-1404">
              Anything smaller fails scans in dim lab light.
            </p>
            <div className="bg-white border border-film-border p-3 flex items-center justify-center" data-id="element-1405">
              <MiniQR size="md" data-id="element-1406" />
            </div>
          </div>

          {/* Quiet zone */}
          <div className="bg-white border border-film-border p-6 shadow-card relative" data-id="element-1407">
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{
            backgroundColor: C_AMBER
          }} data-id="element-1408" />
            <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-2" data-id="element-1409">
              Rule 02 · Quiet zone
            </p>
            <p className="font-display font-bold text-3xl text-film-black mb-1" data-id="element-1410">
              4× module
            </p>
            <p className="text-xs text-film-gray leading-snug mb-4" data-id="element-1411">
              White space around the QR equals 4 module widths.
            </p>
            <div className="bg-white border border-film-border p-3 flex items-center justify-center" data-id="element-1412">
              <div className="bg-white p-4 border border-dashed" style={{
              borderColor: C_AMBER + '80'
            }} data-id="element-1413">
                <MiniQR size="sm" data-id="element-1414" />
              </div>
            </div>
          </div>

          {/* Contrast */}
          <div className="bg-white border border-film-border p-6 shadow-card relative" data-id="element-1415">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-film-black" data-id="element-1416" />
            <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-2" data-id="element-1417">
              Rule 03 · Contrast
            </p>
            <p className="font-display font-bold text-2xl text-film-black mb-1 leading-tight" data-id="element-1418">
              Black on white
            </p>
            <p className="text-xs text-film-gray leading-snug mb-4" data-id="element-1419">
              Never on photo. Never on red. Highest contrast wins.
            </p>
            <div className="flex gap-2" data-id="element-1420">
              <div className="flex-1 bg-white border p-3 flex items-center justify-center gap-1.5" style={{
              borderColor: C_GREEN
            }} data-id="element-1421">
                <Check className="w-3 h-3" style={{
                color: C_GREEN
              }} data-id="element-1422" />
                <span className="font-mono text-[8px] uppercase tracking-wider" style={{
                color: C_GREEN
              }} data-id="element-1423">
                  OK
                </span>
              </div>
              <div className="flex-1 bg-film-red p-3 flex items-center justify-center gap-1.5" data-id="element-1424">
                <X className="w-3 h-3 text-white" data-id="element-1425" />
                <span className="font-mono text-[8px] text-white uppercase tracking-wider" data-id="element-1426">
                  No
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PRINT SPECS · expanded                        */}
      {/* ============================================ */}
      <section data-id="element-1427">
        <SectionHeader id="§ PRINT_SPECS" title="Print Specs" sub="Defaults for every print job. Send to any vendor — they'll know what to do." data-id="element-1428" />

        {/* Base specs grid */}
        <div className="bg-white border border-film-border shadow-card mb-6" data-id="element-1429">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6" data-id="element-1430">
            {[['Paper', '350gsm uncoated'], ['Finish', 'Matte'], ['Bleed', '3mm'], ['Color mode', 'CMYK'], ['Resolution', '300 dpi'], ['Cut tolerance', '±1mm']].map(([label, value], i) => <div key={label} className={`p-5 ${i < 5 ? 'border-b md:border-b-0 md:border-r' : ''} border-film-border`} data-id="element-1431">
                <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray mb-1.5" data-id="element-1432">
                  {label}
                </p>
                <p className="font-mono text-sm text-film-black" data-id="element-1433">{value}</p>
              </div>)}
          </div>
        </div>

        {/* Ink colours CMYK breakdown */}
        <div className="bg-white border border-film-border shadow-card mb-6" data-id="element-1434">
          <div className="px-5 py-3 border-b border-film-border bg-film-cream" data-id="element-1435">
            <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray" data-id="element-1436">
              Ink build · CMYK targets
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4" data-id="element-1437">
            {[{
            name: 'fn-red',
            hex: '#C93B3B',
            cmyk: 'C5 M85 Y75 K0'
          }, {
            name: 'fn-orange',
            hex: '#FF8C42',
            cmyk: 'C0 M55 Y80 K0'
          }, {
            name: 'fn-black',
            hex: '#2B2B2B',
            cmyk: 'C60 M50 Y40 K100'
          }, {
            name: 'fn-cream',
            hex: '#F5F5F0',
            cmyk: 'C2 M2 Y6 K0'
          }, {
            name: 'kodak-yellow',
            hex: C_YELLOW,
            cmyk: 'C3 M28 Y90 K0'
          }, {
            name: 'safelight-amber',
            hex: C_AMBER,
            cmyk: 'C8 M68 Y95 K0'
          }, {
            name: 'darkroom-burgundy',
            hex: C_BURGUNDY,
            cmyk: 'C30 M90 Y65 K30'
          }, {
            name: 'kodachrome-violet',
            hex: C_VIOLET,
            cmyk: 'C75 M85 Y20 K10'
          }, {
            name: 'fuji-green',
            hex: C_GREEN,
            cmyk: 'C82 M30 Y90 K20'
          }, {
            name: 'ektar-blue',
            hex: C_BLUE,
            cmyk: 'C90 M68 Y20 K10'
          }].map((c, i) => <div key={c.name} className={`p-4 flex items-center gap-3 ${i % 4 < 3 ? 'md:border-r' : ''} border-b border-film-border`} data-id="element-1438">
                <div className="w-8 h-8 shrink-0" style={{
              backgroundColor: c.hex
            }} data-id="element-1439" />
                <div className="min-w-0" data-id="element-1440">
                  <p className="font-mono text-[10px] text-film-black truncate" data-id="element-1441">
                    {c.name}
                  </p>
                  <p className="font-mono text-[9px] text-film-gray" data-id="element-1442">
                    {c.cmyk}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Material recipes */}
        <div className="bg-white border border-film-border shadow-card" data-id="element-1443">
          <div className="px-5 py-3 border-b border-film-border bg-film-cream" data-id="element-1444">
            <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray" data-id="element-1445">
              Material recipes · by piece
            </p>
          </div>
          <div className="divide-y divide-film-border" data-id="element-1446">
            {[{
            piece: 'Lab Cards · A6 / 4:5 / A5',
            stock: '350gsm uncoated',
            finish: 'Matte · soft-touch optional',
            notes: 'CMYK · 3mm bleed · 1mm safe edge'
          }, {
            piece: 'Lab Cards · Square',
            stock: '400gsm uncoated',
            finish: 'Matte · slight texture',
            notes: 'Bigger weight to feel premium at small size'
          }, {
            piece: 'Stickers · Opaque',
            stock: 'Matte vinyl 80μm',
            finish: 'Laminated · weather-resistant',
            notes: 'Die-cut to artwork edge · 2mm bleed'
          }, {
            piece: 'Stickers · Clear vinyl',
            stock: 'Transparent vinyl 80μm',
            finish: 'Gloss laminate optional',
            notes: 'Print white-ink underlay where needed for opacity'
          }, {
            piece: 'Postcards',
            stock: '300gsm coated one side',
            finish: 'Front gloss · back uncoated for writing',
            notes: 'Avoid full ink coverage on writable side'
          }].map(r => <div key={r.piece} className="grid grid-cols-12 px-5 py-3 text-[11px] items-center" data-id="element-1447">
                <div className="col-span-3 font-display font-bold text-film-black text-xs" data-id="element-1448">
                  {r.piece}
                </div>
                <div className="col-span-3 font-mono text-[10px] text-film-black" data-id="element-1449">
                  {r.stock}
                </div>
                <div className="col-span-3 font-mono text-[10px] text-film-black" data-id="element-1450">
                  {r.finish}
                </div>
                <div className="col-span-3 text-film-gray leading-snug" data-id="element-1451">
                  {r.notes}
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}